// =============================================================================
// Auth Store - Zustand
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import type { User, AuthResponse } from '../types';
import { useWalletStore } from './wallet';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  linkWallet: (publicKey: string) => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email: string, password: string) => {
        const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', {
          email,
          password,
        });

        const { user, token } = response.data.data;

        // Set auth header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Fetch wallet balance now that we have auth token
        setTimeout(() => {
          useWalletStore.getState().fetchBalance();
        }, 100);
      },

      register: async (data: RegisterData) => {
        const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/register', data);

        const { user, token } = response.data.data;

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        delete api.defaults.headers.common['Authorization'];

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: async () => {
        const token = get().token;

        if (!token) {
          set({ isLoading: false });
          return;
        }

        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get<{ success: boolean; data: User }>('/auth/me');

          set({
            user: response.data.data,
            isAuthenticated: true,
            isLoading: false,
          });
          
          // Fetch wallet balance now that we verified auth
          setTimeout(() => {
            useWalletStore.getState().fetchBalance();
          }, 100);
        } catch {
          // Token invalid or expired
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          delete api.defaults.headers.common['Authorization'];
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },

      linkWallet: async (publicKey: string) => {
        const response = await api.post<{ user: User }>('/auth/link-wallet', {
          publicKey,
        });

        set({ user: response.data.user });
      },
    }),
    {
      name: 'cewce-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
    }
  )
);
